import { DownloadOption } from "./DownloadOption";
import { RadioGroup } from "react-migration/components/RadioGroup";
import classNames from "classnames";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { PaymentSummary, PaymentSummaryUS } from "../components/PaymentSummary";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";

export enum SendOption {
  Send = "send",
  Download = "download",
}

export type SendOptionsProps = {
  sendOption: SendOption;
  onSendOptionChange: (option: SendOption) => void;
  setBalance: (balance: number) => void;
  setLetterCost: (totalCost: number) => void;
};

export const SendOptions = ({ sendOption, onSendOptionChange, setBalance, setLetterCost }: SendOptionsProps) => {
  const { t } = useTranslation();

  const isUsUser = hasFeature(Feature.usAccess);
  const PaymentSummaryComponent = isUsUser ? PaymentSummaryUS : PaymentSummary;

  return (
    <div
      className={classNames(
        "flex",
        "grow",
        "overflow-hidden",
        "bg-background-surface-neutral",
        "justify-center",
        "p-4"
      )}
      data-testid="confirm-and-send-content-container"
    >
      <div data-testid="confirm-and-send-content" className={classNames("flex", "max-w-[1144px]", "flex-col", "grow")}>
        <div className={classNames("flex", "flex-col")} data-testid="confirm-and-send-header-section">
          <span className={classNames("text-2xl", "font-semibold", "text-text-base-primary")}>
            {t("sites.letters.confirm_and_send")}
          </span>
          <span className={classNames("text-base", "font-semibold", "text-text-base-secondary", "mt-7", "mb-3")}>
            {t("sites.letters.how_would_you_like_to_send_your_letter")}
          </span>
        </div>
        <div className={classNames("flex", "flex-row", "gap-5")}>
          <div className={classNames("flex", "flex-col", "gap-5", "grow")}>
            <div
              data-testid="confirm-and-send-select-section"
              className={classNames("bg-background-surface", "border", "border-border-action", "rounded-lg")}
            >
              <RadioGroup value={sendOption} onValueChange={onSendOptionChange}>
                <div
                  className={classNames(
                    "flex",
                    "flex-row",
                    "items-center",
                    "gap-3",
                    "p-4",
                    "border-b",
                    "border-border-action",
                    "cursor-pointer"
                  )}
                  data-testid="send-option-send"
                  onClick={() => {
                    onSendOptionChange(SendOption.Send);
                  }}
                >
                  <RadioGroup.Item value={SendOption.Send} className="h-4! w-4!">
                    <RadioGroup.Indicator />
                  </RadioGroup.Item>
                  <div
                    className={classNames(
                      "h-10",
                      "w-10",
                      "flex",
                      "items-center",
                      "justify-center",
                      "border-platform-50",
                      "bg-platform-100",
                      "bg-platform-100",
                      "border-4",
                      "rounded-full"
                    )}
                  >
                    <i className={classNames("icon-lt-mail-send-line", "text-base", "text-platform-600")} />
                  </div>
                  <div className={classNames("flex", "flex-col")}>
                    <span className={classNames("text-text-base-primary", "text-sm", "font-semibold", "leading-5")}>
                      {t("sites.letters.send_via_company_name")}
                    </span>
                    <span className={classNames("text-text-base-secondary", "text-sm", "font-normal", "leading-5")}>
                      {t("sites.letters.save_time_by_letting_us_post_the_letter_for_you")}
                    </span>
                  </div>
                </div>
                <DownloadOption
                  onClick={() => {
                    onSendOptionChange(SendOption.Download);
                  }}
                />
              </RadioGroup>
            </div>
          </div>
          <div data-testid="confirm-and-send-letter-summary-section">
            <PaymentSummaryComponent
              onBalanceLoaded={setBalance}
              onTotalCostCalculated={setLetterCost}
              sendOption={sendOption}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
